/**
 * Rules data hooks + rule editing helpers (FE-4, PHASE1 §4.1/§7.3).
 *
 * The single `['rules']` cache holds the full rule list (priority-ordered by the
 * server). All create/update/delete/reorder mutations invalidate it;
 * apply-to-existing additionally invalidates `['transactions']` + `['inbox']` +
 * `['reports']` + `['budgets']` because it rewrites splits and therefore the
 * per-bucket actuals every report/budget view aggregates (§6.6). Dry-run is a
 * keyless, non-mutating preview.
 *
 * This module also owns the non-component editing helpers (editable-condition
 * bookkeeping, rule summaries, prefill-from-transaction) so the rule dialog
 * components can stay export-only-components for React Fast Refresh.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk } from '../queryKeys.ts';
import { CONDITION_FIELD, CONDITION_OP } from '../labels.ts';
import type {
  InboxGroup,
  ReorderRequest,
  RuleApplyIn,
  RuleApplyResult,
  RuleCondition,
  RuleCreate,
  RuleDryRunIn,
  RuleDryRunResult,
  RuleMatchMode,
  RuleOut,
  RuleUpdate,
  TransactionOut,
} from '../types.ts';

const STALE = 5 * 60 * 1000;

// ── Queries ────────────────────────────────────────────────────────────────

/** All rules, priority-ordered (server order priority,id). */
export function useRules(): UseQueryResult<RuleOut[]> {
  return useQuery({
    queryKey: qk.rules,
    queryFn: () => api.get<RuleOut[]>('/api/rules?active_only=false'),
    staleTime: STALE,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────

export function useCreateRule(): UseMutationResult<RuleOut, Error, RuleCreate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RuleCreate) => api.post<RuleOut>('/api/rules', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.rules });
    },
  });
}

export function useUpdateRule(): UseMutationResult<
  RuleOut,
  Error,
  { id: number; body: RuleUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: RuleUpdate }) =>
      api.patch<RuleOut>(`/api/rules/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.rules });
    },
  });
}

export function useDeleteRule(): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/api/rules/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.rules });
    },
  });
}

export function useReorderRules(): UseMutationResult<RuleOut[], Error, ReorderRequest> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReorderRequest) => api.post<RuleOut[]>('/api/rules/reorder', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.rules });
    },
  });
}

/** Non-mutating dry-run preview — keyless. */
export function useRuleDryRun(): UseMutationResult<RuleDryRunResult, Error, RuleDryRunIn> {
  return useMutation({
    mutationFn: (body: RuleDryRunIn) => api.post<RuleDryRunResult>('/api/rules/dry-run', body),
  });
}

/** Apply an existing rule to existing transactions (provenance-respecting). */
export function useApplyRule(): UseMutationResult<
  RuleApplyResult,
  Error,
  { id: number; body: RuleApplyIn }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: RuleApplyIn }) =>
      api.post<RuleApplyResult>(`/api/rules/${id}/apply`, body),
    onSuccess: () => {
      // Bulk recategorization changes per-bucket actuals — the same set every
      // other split-writing mutation invalidates (§6.6).
      void qc.invalidateQueries({ queryKey: qk.transactions });
      void qc.invalidateQueries({ queryKey: qk.inbox });
      void qc.invalidateQueries({ queryKey: qk.reports });
      void qc.invalidateQueries({ queryKey: qk.budgets });
    },
  });
}

// ── Editable-condition bookkeeping ─────────────────────────────────────────

/** A rule condition carrying a stable client key for React list rendering. */
export interface EditableCondition extends RuleCondition {
  _key: string;
}

let keySeq = 0;
function nextKey(): string {
  return `cond-${keySeq++}`;
}

/** A fresh blank condition row (defaults to a payee-contains text condition). */
export function blankCondition(): EditableCondition {
  return { _key: nextKey(), field: 'payee', op: 'contains', value: '' };
}

/** Wrap wire conditions with client keys for editing. */
export function toEditableConditions(conditions: RuleCondition[]): EditableCondition[] {
  return conditions.map((c) => ({ ...c, _key: nextKey() }));
}

/** Strip client keys + empty-value rows and trim values for the wire payload. */
export function fromEditableConditions(conditions: EditableCondition[]): RuleCondition[] {
  return conditions
    .filter((c) => c.value.trim() !== '')
    .map(({ field, op, value }) => ({ field, op, value: value.trim() }));
}

// ── Human-readable summaries (German) ──────────────────────────────────────

const MODE_WORD: Record<RuleMatchMode, string> = { all: 'UND', any: 'ODER' };

/** e.g. `Empfänger enthält „REWE“ UND Betrag größer als 10`. */
export function conditionSummary(rule: RuleOut): string {
  if (rule.conditions.length === 0) return 'Keine Bedingungen';
  const parts = rule.conditions.map(
    (c) => `${CONDITION_FIELD[c.field]} ${CONDITION_OP[c.op]} „${c.value}“`,
  );
  return parts.join(` ${MODE_WORD[rule.match_mode]} `);
}

/** e.g. `Kategorie → Lebensmittel · Tags: bar`. */
export function actionSummary(
  rule: RuleOut,
  bucketName: (id: number) => string | undefined,
): string {
  const parts: string[] = [];
  const a = rule.actions;
  if (a.set_bucket_id != null) {
    parts.push(`Kategorie → ${bucketName(a.set_bucket_id) ?? `#${a.set_bucket_id}`}`);
  }
  if (a.rename_payee) parts.push(`Empfänger → „${a.rename_payee}“`);
  if (a.add_tags && a.add_tags.length > 0) parts.push(`Tags: ${a.add_tags.join(', ')}`);
  return parts.length > 0 ? parts.join(' · ') : 'Keine Aktion';
}

/** Build a RuleCreate prefilled from a transaction (CreateRuleFromTransaction). */
export function ruleInitialFromTransaction(txn: TransactionOut): RuleCreate {
  const conditions: RuleCondition[] = [];
  const payee = txn.normalized_payee?.trim() || txn.counterparty_name?.trim();
  if (payee) {
    conditions.push({ field: 'payee', op: 'contains', value: payee });
  } else if (txn.purpose?.trim()) {
    conditions.push({ field: 'purpose', op: 'contains', value: txn.purpose.trim() });
  }
  return {
    name: payee ? `Regel: ${payee}` : 'Neue Regel',
    match_mode: 'all',
    conditions,
    actions: {
      set_bucket_id: txn.primary_bucket_id ?? null,
      rename_payee: null,
      add_tags: [],
    },
    priority: 100,
    stop_processing: false,
    active: true,
  };
}

/**
 * Build a RuleCreate prefilled from a detected inbox group (`GroupBanner` →
 * "Als Regel speichern"): a payee-contains condition, plus a purpose-contains
 * condition on the PSP sub-merchant when the group has one (so e.g. a PayPal
 * "spotify" subgroup doesn't loosely match every PayPal transaction). `bucketId`
 * is resolved by the caller (the group's own suggestion, else the last group
 * action's bucket) — this function only wires it into the action.
 */
export function ruleInitialFromGroup(group: InboxGroup, bucketId: number | null): RuleCreate {
  const conditions: RuleCondition[] = [
    { field: 'payee', op: 'contains', value: group.normalized_payee },
  ];
  if (group.sub_merchant) {
    conditions.push({ field: 'purpose', op: 'contains', value: group.sub_merchant });
  }
  return {
    name: `Serie: ${group.display_name}`,
    match_mode: 'all',
    conditions,
    actions: {
      set_bucket_id: bucketId,
      rename_payee: null,
      add_tags: [],
    },
    priority: 100,
    stop_processing: false,
    active: true,
  };
}
